import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as PR from "../../prime-modules/index";
import AdminHeader from "../layout/admin-header";
import AdminFooter from "../layout/admin-footer";
import "./KycCountries.scss";
import { getCountriesList, getKycCountries, addKycCountry, deleteKycCountry } from "../../services/api";

const KycCountries = () => {
    const dispatch = useDispatch();
    const adminData = useSelector((state) => state.adminAuth.adminSessionData);
    const headers = useMemo(() => {
        return { sessionid: adminData.sessionId };
    }, [adminData.sessionId]);

    const toast = useRef(null);

    const [allCountries, setAllCountries] = useState([]);
    const [kycCountries, setKycCountries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIso, setSelectedIso] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [countryToDelete, setCountryToDelete] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Fetch all available countries for dropdown (only local countries)
    const fetchAllCountries = useCallback(() => {
        getCountriesList(headers, dispatch, (response) => {
            if (response.result === "SUCCESS" && Array.isArray(response.data)) {
                const localCountries = response.data.filter(
                    (c) => !c.geogType || c.geogType === "Local" || c.geogType?.toLowerCase() === "local"
                );
                const formatted = localCountries.map((c) => ({
                    label: `${c.name?.split('(')[0]?.trim() || c.iso} (${c.iso})`,
                    value: c.iso,
                    name: c.name?.split('(')[0]?.trim() || c.iso,
                    iso: c.iso
                }));
                // Sort alphabetically by country name
                formatted.sort((a, b) => a.name.localeCompare(b.name));
                setAllCountries(formatted);
            }
        });
    }, [dispatch, headers]);

    // Fetch KYC required countries list (GET /v1/kyc/countries)
    const fetchKycCountries = useCallback(() => {
        setLoading(true);
        getKycCountries(headers, dispatch, (response) => {
            setLoading(false);
            if (response.result === "SUCCESS") {
                const data = Array.isArray(response.data) ? response.data : [];
                setKycCountries(data);
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: response.error?.errorMsg || "Failed to fetch KYC required countries"
                });
            }
        });
    }, [dispatch, headers]);

    useEffect(() => {
        if (adminData.sessionId) {
            fetchAllCountries();
            fetchKycCountries();
        }
    }, [adminData.sessionId, fetchAllCountries, fetchKycCountries]);

    // Add country to KYC list (POST /v1/kyc/countries with { iso })
    const handleAddKycCountry = () => {
        if (!selectedIso) {
            toast.current?.show({
                severity: "warn",
                summary: "Warning",
                detail: "Please select a country from the dropdown"
            });
            return;
        }

        // Check if country is already in the list
        const alreadyExists = kycCountries.some(
            (c) => c.iso?.toUpperCase() === selectedIso.toUpperCase()
        );
        if (alreadyExists) {
            toast.current?.show({
                severity: "warn",
                summary: "Warning",
                detail: "Country is already in the KYC required list"
            });
            return;
        }

        setActionLoading(true);
        addKycCountry({ iso: selectedIso }, headers, dispatch, (response) => {
            setActionLoading(false);
            if (response.result === "SUCCESS") {
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Country added to KYC required list successfully",
                    life: 3000
                });
                setSelectedIso("");
                fetchKycCountries();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: response.error?.errorMsg || "Failed to add country to KYC list"
                });
            }
        });
    };

    // Confirm Delete
    const confirmDelete = (country) => {
        setCountryToDelete(country);
        setDeleteDialog(true);
    };

    // Delete country from KYC list (DELETE /v1/kyc/countries/{iso})
    const handleDeleteKycCountry = () => {
        if (!countryToDelete || !countryToDelete.iso) return;

        setActionLoading(true);
        deleteKycCountry(countryToDelete.iso, headers, dispatch, (response) => {
            setActionLoading(false);
            setDeleteDialog(false);
            if (response.result === "SUCCESS") {
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `Country ${countryToDelete.iso} removed from KYC required list`,
                    life: 3000
                });
                setCountryToDelete(null);
                fetchKycCountries();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: response.error?.errorMsg || "Failed to delete country from KYC list"
                });
            }
        });
    };

    // Helper map to look up full country name if GET response only has ISO or needs fallback
    const countryNameMap = useMemo(() => {
        const map = {};
        allCountries.forEach((c) => {
            map[c.iso] = c.name;
        });
        return map;
    }, [allCountries]);

    // Filtered KYC countries based on search query
    const filteredKycCountries = useMemo(() => {
        if (!searchQuery.trim()) return kycCountries;
        const q = searchQuery.toLowerCase().trim();
        return kycCountries.filter((item) => {
            const iso = item.iso ? item.iso.toLowerCase() : "";
            const name = item.name
                ? item.name.toLowerCase()
                : (countryNameMap[item.iso] || "").toLowerCase();
            return iso.includes(q) || name.includes(q);
        });
    }, [kycCountries, searchQuery, countryNameMap]);

    // Item template for DataView
    const itemTemplate = (rowData) => {
        const displayName = rowData.name || countryNameMap[rowData.iso] || rowData.iso;

        return (
            <div className="col-12 custom-bg" key={rowData.iso}>
                <div className="custom-table-body">
                    <div className="table-grid">
                        <ul className="col-ul mobile-col-ul">
                            <li className="iso-code">
                                <div className="column-label">ISO Code</div>
                                <div className="column-data font-bold">{rowData.iso}</div>
                            </li>
                            <li className="country-name">
                                <div className="column-label">Country Name</div>
                                <div className="column-data">{displayName}</div>
                            </li>
                            <li className="actions-tab">
                                <div className="column-label">Actions</div>
                                <div className="column-data">
                                    <PR.Button
                                        icon="pi pi-trash"
                                        rounded
                                        outlined
                                        severity="danger"
                                        className="icon-button delete"
                                        onClick={() => confirmDelete(rowData)}
                                        tooltip="Delete Country"
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="main">
            <div className="layout-sidebar">
                <AdminHeader />
            </div>
            <div className="layout-content-wrapper relative">
                <section className="kyc-countries-container">
                    <div className="grid grid-nogutter mb-4">
                        <div className="col-12">
                            <div className="heading-sec">
                                <h1>KYC Required Countries</h1>
                            </div>
                        </div>
                    </div>

                    {/* Add Country Controls */}
                    <div className="card mb-4 add-kyc-card">
                        <div className="flex flex-column sm:flex-row align-items-stretch sm:align-items-center gap-3">
                            <h2 className="text-lg font-semibold m-0 white-space-nowrap mr-2">Add New Country</h2>
                            <div className="w-full sm:w-22rem">
                                <PR.Dropdown
                                    value={selectedIso}
                                    options={allCountries}
                                    onChange={(e) => setSelectedIso(e.value)}
                                    placeholder="Select a Country"
                                    filter
                                    showClear={!!selectedIso}
                                    filterBy="label,name,iso"
                                    className="w-full country-dropdown"
                                />
                            </div>
                            <div>
                                <PR.Button
                                    label="Add KYC Country"
                                    icon="pi pi-plus"
                                    className="add-button w-full sm:w-auto"
                                    onClick={handleAddKycCountry}
                                    loading={actionLoading}
                                    disabled={!selectedIso || actionLoading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Search & Filter Header */}
                    <div className="flex flex-column md:flex-row align-items-stretch md:align-items-center justify-content-between mb-4 gap-3">
                        <div className="search-input-wrapper">
                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-search" />
                                <PR.InputText
                                    placeholder="Search by Country Name or ISO..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full search-input"
                                />
                            </span>
                        </div>
                        <div className="text-right text-sm count-badge">
                            Total KYC Countries: <strong>{kycCountries.length}</strong>
                        </div>
                    </div>

                    {/* KYC Countries List */}
                    <div className="kyc-data-table card">
                        <div className="card datatable-scroll">
                            <div className="custom-table">
                                <div className="custom-table-header">
                                    <div className="table-grid">
                                        <ul className="col-ul">
                                            <li className="iso-code"><span>ISO Code</span></li>
                                            <li className="country-name"><span>Country Name</span></li>
                                            <li className="actions-tab"><span>Actions</span></li>
                                        </ul>
                                    </div>
                                </div>
                                <PR.DataView
                                    value={filteredKycCountries}
                                    itemTemplate={itemTemplate}
                                    loading={loading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Confirmation Dialog for Deleting KYC Country */}
                    <PR.Dialog
                        visible={deleteDialog}
                        header="Confirm Deletion"
                        className="delete-kyc-dialog"
                        draggable={false}
                        resizable={false}
                        modal
                        onHide={() => setDeleteDialog(false)}
                    >
                        <div className="confirmation-content flex align-items-center">
                            <i className="pi pi-exclamation-triangle mr-3 text-red-500" style={{ fontSize: "2rem" }} />
                            {countryToDelete && (
                                <span>
                                    Are you sure you want to remove <b>{countryToDelete.name || countryNameMap[countryToDelete.iso] || countryToDelete.iso} ({countryToDelete.iso})</b> from the KYC required countries list?
                                </span>
                            )}
                        </div>
                        <div className="flex justify-content-end mt-4 buttons-sections gap-2">
                            <PR.Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text confirm-button reset-btn"
                                onClick={() => setDeleteDialog(false)}
                                disabled={actionLoading}
                            />
                            <PR.Button
                                label="Delete"
                                icon="pi pi-trash"
                                severity="danger"
                                className="confirm-button yes"
                                onClick={handleDeleteKycCountry}
                                loading={actionLoading}
                            />
                        </div>
                    </PR.Dialog>
                </section>
                <AdminFooter />
            </div>
            <PR.Toast ref={toast} position="top-right" />
        </div>
    );
};

export default KycCountries;
